import { useState, useEffect, useContext } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { AppContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, Eye, X } from 'lucide-react';
import { format } from 'date-fns';
import { useMembershipStatus, MembershipBlockedOverlay } from '../components/MembershipGuard';
import { PageSpinner } from '../components/PageSpinner';
import { Pagination } from '../components/Pagination';

const ITEMS_PER_PAGE = 15;

export default function ConfigConsentimientos() {
  const { API_URL } = useContext(AppContext);
  const { isBlocked, loading: membershipLoading } = useMembershipStatus();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templatesError, setTemplatesError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [formData, setFormData] = useState({
    nombre_procedimiento: '',
    politicas_centro: '',
    politicas_procedimiento: ''
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setTemplatesError(false);
    try {
      const token = localStorage.getItem('konemed_token');
      const response = await axios.get(`${API_URL}/consents/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (error) {
      // Distinguish "failed to load" from "genuinely no templates" — showing the empty
      // state here would wrongly tell the user to go create a template that already exists.
      setTemplatesError(true);
      toast.error('Error al cargar plantillas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('konemed_token');
      
      if (editingTemplate) {
        await axios.put(`${API_URL}/consents/templates/${editingTemplate.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Plantilla actualizada');
      } else {
        await axios.post(`${API_URL}/consents/templates`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Plantilla creada');
      }
      
      setShowModal(false);
      setEditingTemplate(null);
      setFormData({ nombre_procedimiento: '', politicas_centro: '', politicas_procedimiento: '' });
      fetchTemplates();
    } catch (error) {
      toast.error('Error al guardar plantilla');
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      nombre_procedimiento: template.nombre_procedimiento,
      politicas_centro: template.politicas_centro,
      politicas_procedimiento: template.politicas_procedimiento
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta plantilla?')) return;

    try {
      const token = localStorage.getItem('konemed_token');
      await axios.delete(`${API_URL}/consents/templates/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Plantilla eliminada');
      fetchTemplates();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleNewTemplate = () => {
    setEditingTemplate(null);
    setFormData({ nombre_procedimiento: '', politicas_centro: '', politicas_procedimiento: '' });
    setShowModal(true);
  };

  return (
    <DashboardLayout>
      <div data-testid="config-consentimientos-page">
        {membershipLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
          </div>
        ) : isBlocked ? (
          <>
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-slate-900 mb-2" data-testid="page-title">
                Configuración de Consentimientos
              </h1>
              <p className="text-slate-600">Gestiona las plantillas de consentimientos del centro</p>
            </div>
            <MembershipBlockedOverlay mode="full" />
          </>
        ) : (
        <>
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2" data-testid="page-title">
              Configuración de Consentimientos
            </h1>
            <p className="text-slate-600">Gestiona las plantillas de consentimientos del centro</p>
          </div>
          <button
            onClick={handleNewTemplate}
            data-testid="new-template-button"
            className="flex items-center gap-2 px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white font-semibold rounded-lg transition-all shadow-lg shadow-violet-500/20 active:scale-95"
          >
            <Plus size={20} />
            Nuevo Consentimiento
          </button>
        </div>

        {loading ? (
          <PageSpinner />
        ) : templatesError ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-12 text-center" data-testid="templates-error">
            <h3 className="text-xl font-bold text-red-900 mb-2">No se pudieron cargar las plantillas</h3>
            <p className="text-red-700 mb-6">Ocurrió un error al consultar los datos. Intenta nuevamente.</p>
            <button
              onClick={fetchTemplates}
              className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-all"
            >
              Reintentar
            </button>
          </div>
        ) : templates.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center" data-testid="empty-state">
            <h3 className="text-xl font-bold text-slate-900 mb-2">No hay plantillas configuradas</h3>
            <p className="text-slate-600 mb-6">Crea tu primera plantilla de consentimiento</p>
            <button
              onClick={handleNewTemplate}
              className="px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white font-semibold rounded-lg transition-all"
            >
              Crear Plantilla
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="templates-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Nombre del Procedimiento</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Fecha de Creación</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-slate-900">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {templates.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((template) => (
                    <tr key={template.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-slate-900 font-medium">
                        {template.nombre_procedimiento}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {template.created_at ? format(new Date(template.created_at), 'dd/MM/yyyy HH:mm') : 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleEdit(template)}
                            data-testid={`edit-button-${template.id}`}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Editar"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(template.id)}
                            data-testid={`delete-button-${template.id}`}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-4">
              <Pagination currentPage={currentPage} totalPages={Math.ceil(templates.length / ITEMS_PER_PAGE)} onPageChange={setCurrentPage} />
            </div>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="template-modal">
            <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-slate-200 px-8 py-6 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-slate-900">
                  {editingTemplate ? 'Editar Plantilla' : 'Nueva Plantilla'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  data-testid="close-modal-button"
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-8 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Nombre del Procedimiento
                  </label>
                  <input
                    type="text"
                    value={formData.nombre_procedimiento}
                    onChange={(e) => setFormData({ ...formData, nombre_procedimiento: e.target.value })}
                    data-testid="input-nombre-procedimiento"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-slate-50"
                    placeholder="Ej: Cirugía General"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Políticas del Centro
                  </label>
                  <textarea
                    value={formData.politicas_centro}
                    onChange={(e) => setFormData({ ...formData, politicas_centro: e.target.value })}
                    data-testid="input-politicas-centro"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-slate-50 min-h-32"
                    placeholder="Ingrese las políticas generales del centro..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Políticas de Consentimiento de Procedimiento
                  </label>
                  <textarea
                    value={formData.politicas_procedimiento}
                    onChange={(e) => setFormData({ ...formData, politicas_procedimiento: e.target.value })}
                    data-testid="input-politicas-procedimiento"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-slate-50 min-h-32"
                    placeholder="Ingrese las políticas específicas del procedimiento..."
                    required
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-900 font-semibold rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    data-testid="save-template-button"
                    className="flex-1 px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white font-semibold rounded-lg transition-all shadow-lg shadow-violet-500/20 active:scale-95"
                  >
                    {editingTemplate ? 'Actualizar' : 'Crear'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        </>
        )}
      </div>
    </DashboardLayout>
  );
}
